import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ADJUSTMENT_KINDS,
  ADJUSTMENT_KIND_MAP,
  PAYROLL_RUN_STATUS_MAP,
  periodLabel,
  type AdjustmentKind,
  type PayrollRunStatus,
  type PayslipLine,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date, money } from '../../lib/format';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface Run {
  id: string;
  period: string;
  periodLabel: string;
  status: PayrollRunStatus;
  grossTotal: string;
  deductions: string;
  netTotal: string;
  payslipCount: number;
  approvedAt: string | null;
  paidAt: string | null;
}

interface Payslip {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string | null;
  jobTitle: string | null;
  department: string | null;
  baseSalary: string;
  additions: string;
  deductions: string;
  netPay: string;
  breakdown: PayslipLine[];
  presentDays: string;
  absentDays: string;
  leaveDays: string;
}

/** أول يوم في الشهر الماضي — أقرب شهر مكتمل تُفتح له دورة. */
function lastMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
    .toISOString()
    .slice(0, 10);
}

/* ==========================================================================
   قائمة الدورات
   ========================================================================== */

export function PayrollRuns() {
  const [creating, setCreating] = useState(false);
  const [adjusting, setAdjusting] = useState(false);

  const runs = useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: () => api<Run[]>('/hr/payroll/runs'),
  });

  return (
    <div className="page">
      <PageHead
        title="المرتبات"
        subtitle="دورات المرتب الشهرية"
        actions={
          <>
            <button className="btn" onClick={() => setAdjusting(true)}>
              إضافة بند
            </button>
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              دورة جديدة
            </button>
          </>
        }
      />

      {runs.isError && <ErrorState error={runs.error} onRetry={() => runs.refetch()} />}

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الشهر</th>
                <th>الحالة</th>
                <th>القسائم</th>
                <th>الإجمالي</th>
                <th>الخصومات</th>
                <th>الصافي</th>
              </tr>
            </thead>
            {runs.isLoading && <TableSkeleton rows={4} cols={6} />}

            <tbody>
              {runs.data?.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/hr/payroll/${r.id}`} className="cell-strong link">
                      {r.periodLabel}
                    </Link>
                  </td>
                  <td>
                    <Badge kind={PAYROLL_RUN_STATUS_MAP[r.status].kind}>
                      {PAYROLL_RUN_STATUS_MAP[r.status].label}
                    </Badge>
                  </td>
                  <td className="num cell-mute">{r.payslipCount}</td>
                  <td className="num">{money(r.grossTotal)}</td>
                  <td className="num neg">{money(r.deductions)}</td>
                  <td className="num cell-strong">{money(r.netTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!runs.isLoading && runs.data?.length === 0 && (
          <EmptyState
            title="لا دورات مرتب بعد"
            text="افتح دورة لشهر مضى لتُحسب قسائم الفريق من حضورهم وبنودهم."
          />
        )}
      </div>

      {creating && <CreateRunDialog onClose={() => setCreating(false)} />}
      {adjusting && <AdjustmentDialog onClose={() => setAdjusting(false)} />}
    </div>
  );
}

function CreateRunDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState(lastMonth().slice(0, 7));

  const create = useMutation({
    mutationFn: () =>
      api<Run>('/hr/payroll/runs', { method: 'POST', body: { period: `${period}-01` } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      onClose();
    },
  });

  return (
    <Modal
      title="دورة مرتب جديدة"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            {create.isPending ? 'جارٍ الحساب' : 'إنشاء وحساب'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الإنشاء.'}
        </div>
      )}

      <div className="field">
        <label className="label req" htmlFor="p-period">
          شهر المرتب
        </label>
        <input
          id="p-period"
          className="input num"
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
      </div>

      {/*
        الحساب يقع مرة واحدة وتُجمَّد نتيجته.
        حسابه عند كل عرض كان سيجعل قسيمة شهر مضى تتغيّر كلما صُحّح حضور
        قديم أو رُفع مرتب — فيختلف ما يراه الموظف عمّا قبضه.
      */}
      <div className="alert alert-info mt-4">
        تُحسب قسائم كل من هو على رأس العمل من حضورهم وبنودهم في هذا الشهر، وتُجمَّد النتيجة.
        تعديل مرتب أو حضور بعدها لا يغيّر القسائم المحسوبة.
      </div>
    </Modal>
  );
}

function AdjustmentDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [employeeId, setEmployeeId] = useState('');
  const [kind, setKind] = useState<AdjustmentKind>('BONUS');
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState(lastMonth().slice(0, 7));
  const [reason, setReason] = useState('');

  const employees = useQuery({
    queryKey: ['employees', { pageSize: 200, status: 'ACTIVE' }],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        '/hr/employees?pageSize=200&status=ACTIVE',
      ),
  });

  const create = useMutation({
    mutationFn: () =>
      api('/hr/payroll/adjustments', {
        method: 'POST',
        body: {
          employeeId,
          kind,
          amount: Number(amount),
          period: `${period}-01`,
          reason: reason || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      onClose();
    },
  });

  const def = ADJUSTMENT_KIND_MAP[kind];
  const valid = employeeId && Number(amount) > 0;

  return (
    <Modal
      title="بند على المرتب"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={!valid || create.isPending}
          >
            {create.isPending ? 'جارٍ الإضافة' : 'إضافة'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّرت الإضافة.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="a-employee">
            الموظف
          </label>
          <select
            id="a-employee"
            className="select"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            <option value="">اختر</option>
            {employees.data?.items.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="a-kind">
            النوع
          </label>
          <select
            id="a-kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as AdjustmentKind)}
          >
            {ADJUSTMENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {ADJUSTMENT_KIND_MAP[k].label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="a-amount">
            المبلغ
          </label>
          <input
            id="a-amount"
            className="input num"
            dir="ltr"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {/*
            المبلغ موجب دائمًا والإشارة من النوع.
            قبول السالب كان يجعل "خصمًا بمبلغ سالب" يعني زيادة.
          */}
          <span className={`tiny ${def.sign === 1 ? 'pos' : 'neg'}`}>
            {def.sign === 1 ? 'يُضاف إلى المرتب' : 'يُخصم من المرتب'}
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="a-period">
            شهر الاستحقاق
          </label>
          <input
            id="a-period"
            className="input num"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="a-reason">
            السبب
          </label>
          <input
            id="a-reason"
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <span className="tiny mute">يظهر على قسيمة الموظف.</span>
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   تفاصيل الدورة
   ========================================================================== */

export function PayrollRunDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);

  const run = useQuery({
    queryKey: ['payroll', 'run', id],
    queryFn: () => api<Run & { payslips: Payslip[]; notes: string | null }>(`/hr/payroll/runs/${id}`),
  });

  const act = useMutation({
    mutationFn: (action: 'approve' | 'paid') =>
      api(`/hr/payroll/runs/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payroll'] }),
  });

  const remove = useMutation({
    mutationFn: () => api(`/hr/payroll/runs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      window.location.href = '/hr/payroll';
    },
  });

  if (run.isError) return <ErrorState error={run.error} onRetry={() => run.refetch()} />;
  const data = run.data;

  return (
    <div className="page">
      <PageHead
        title={data ? `مرتبات ${data.periodLabel}` : 'دورة مرتب'}
        subtitle={data ? `${data.payslips.length} قسيمة` : undefined}
        actions={
          data && (
            <>
              <Link to="/hr/payroll" className="btn">
                رجوع
              </Link>
              {data.status === 'DRAFT' && (
                <>
                  <button className="btn btn-danger" onClick={() => remove.mutate()}>
                    حذف المسودة
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => act.mutate('approve')}
                    disabled={act.isPending}
                  >
                    اعتماد
                  </button>
                </>
              )}
              {data.status === 'APPROVED' && (
                <button
                  className="btn btn-primary"
                  onClick={() => act.mutate('paid')}
                  disabled={act.isPending}
                >
                  تسجيل الصرف
                </button>
              )}
            </>
          )
        }
      />

      {act.isError && (
        <div className="alert alert-danger mb-4">
          {act.error instanceof Error ? act.error.message : 'تعذّرت العملية.'}
        </div>
      )}

      {data && (
        <>
          <div className="stats">
            <Stat
              label="الحالة"
              value={
                <Badge kind={PAYROLL_RUN_STATUS_MAP[data.status].kind}>
                  {PAYROLL_RUN_STATUS_MAP[data.status].label}
                </Badge>
              }
            />
            <Stat label="الإجمالي" value={<span className="num">{money(data.grossTotal)}</span>} />
            <Stat
              label="الخصومات"
              value={<span className="num neg">{money(data.deductions)}</span>}
            />
            <Stat label="الصافي" value={<span className="num">{money(data.netTotal)}</span>} />
          </div>

          {data.status === 'DRAFT' && (
            <div className="alert alert-info mb-4">
              مسودة: راجع القسائم قبل الاعتماد. بعد الاعتماد تُقفل الدورة فلا تقبل بنودًا جديدة
              ولا تُحذف.
            </div>
          )}

          <div className="card">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>الموظف</th>
                    <th>الأساسي</th>
                    <th>حضور</th>
                    <th>غياب</th>
                    <th>إضافات</th>
                    <th>خصومات</th>
                    <th>الصافي</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.payslips.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <span className="cell-strong">{p.employeeName}</span>
                        <div className="tiny mute">{p.jobTitle}</div>
                      </td>
                      <td className="num">{money(p.baseSalary)}</td>
                      <td className="num cell-mute">{Number(p.presentDays)}</td>
                      <td className="num cell-mute">{Number(p.absentDays)}</td>
                      <td className="num pos">{money(p.additions)}</td>
                      <td className="num neg">{money(p.deductions)}</td>
                      <td className="num cell-strong">{money(p.netPay)}</td>
                      <td>
                        <button
                          className="btn btn-sm"
                          onClick={() => setOpen(open === p.id ? null : p.id)}
                        >
                          {open === p.id ? 'إخفاء' : 'التفصيل'}
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* التفصيل داخل الجدول لا في نافذة: المراجعة تتنقّل بين الصفوف. */}
                  {data.payslips
                    .filter((p) => p.id === open)
                    .map((p) => (
                      <tr key={`${p.id}-detail`}>
                        <td colSpan={8}>
                          <div className="totals-box">
                            {p.breakdown.map((line, i) => (
                              <div className="totals-row" key={i}>
                                <span className={line.amount < 0 ? 'neg' : ''}>
                                  {line.label}
                                  {line.note && <span className="tiny mute"> — {line.note}</span>}
                                </span>
                                <span className={`num ${line.amount < 0 ? 'neg' : ''}`}>
                                  {money(Math.abs(line.amount))}
                                </span>
                              </div>
                            ))}
                            <div className="totals-row totals-final">
                              <span>الصافي</span>
                              <span className="num">{money(p.netPay)}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {data.payslips.length === 0 && (
              <EmptyState title="لا قسائم في هذه الدورة" text="لا موظفين على رأس العمل." />
            )}
          </div>
        </>
      )}
    </div>
  );
}
